"use client";

import { useCallback, useEffect, useMemo } from "react";
import {
  createParser,
  parseAsArrayOf,
  parseAsInteger,
  parseAsJson,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from "nuqs";
import { z } from "zod";

import type { PipelineEntityFilter } from "@/lib/api/model/pipelineEntityFilter";
import type { PipelineFilterSchema } from "@/lib/api/model/pipelineFilterSchema";
import { toLocalDateTimeToken } from "@/lib/date/day-keys";
import { usePipelineTableStore } from "@/features/jobs/store/pipeline/table";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";

/**
 * Pipeline filter panel state, backed by the URL so a filtered view is
 * shareable and survives a reload — the same approach as the tasks sidebar.
 *
 * Nothing here filters client-side: the selections are compiled into a
 * `PipelineFilterSchema` and posted to `/api/jobs/{id}/pipeline/query`, which
 * evaluates them in SQL. The compiled schema is also mirrored into the pipeline
 * view store so bulk actions and exports resolve the identical candidate set.
 */

/** Mirrors the caps in `app/schemas/pipeline_filters.py`. */
export const MAX_FILTER_TERMS = 25;
export const MAX_FILTER_TERM_LENGTH = 200;
export const MAX_FILTER_IDS = 100;
/**
 * A ceiling on what a bound may *say*, not on who matches: leaving either end
 * off means "no limit" there. The panel's slider spans the job's own range
 * instead of this.
 */
export const MAX_EXPERIENCE_YEARS = 100;

export const ENROLLMENT_STATUS_VALUES = [
  "scheduled",
  "active",
  "paused",
  "completed",
  "cancelled",
  "bounced",
  "error",
] as const;

/**
 * `profile_import` covers the whole `upload_*` family, which is how the origin
 * badge on a table row already reads. The individual file types stay in the
 * union so a link saved before the grouping still parses — the panel no longer
 * offers them, since the backend now counts them under `profile_import`.
 */
export const ORIGIN_VALUES = [
  "sourced",
  "search",
  "ats",
  "profile_import",
  "upload_pdf",
  "upload_docx",
  "upload_rtf",
  "upload_txt",
  "upload_image",
  "upload_csv",
] as const;

/** The file types `profile_import` stands for — see `ORIGIN_VALUES`. */
const PROFILE_IMPORT_ORIGINS: readonly string[] = [
  "upload_pdf",
  "upload_docx",
  "upload_rtf",
  "upload_txt",
  "upload_image",
  "upload_csv",
];

export const ATS_SYNC_STATE_VALUES = [
  "not_synced",
  "queued",
  "synced",
  "error",
  "ineligible",
] as const;

export const SOCIAL_NETWORK_VALUES = [
  "linkedin",
  "github",
  "twitter",
  "facebook",
] as const;

export const RELATIVE_WINDOW_VALUES = [
  "24h",
  "7d",
  "14d",
  "30d",
  "60d",
  "90d",
  "180d",
  "365d",
] as const;

/**
 * Quick ranges offered above the from/to bounds. Each one writes a *relative*
 * lower bound, so a filtered view keeps meaning "the last week" tomorrow rather
 * than freezing the week it was picked. The vocabulary is deliberately a subset
 * of `RELATIVE_WINDOW_VALUES`: a relative bound paired with an open upper one
 * compiles to the backend's own `*_within` field, which it resolves per query.
 */
export const TIME_PRESETS = [
  { window: "24h", label: "Last 24 hours" },
  { window: "7d", label: "Last 7 days" },
  { window: "30d", label: "Last 30 days" },
  { window: "90d", label: "Last 90 days" },
  { window: "365d", label: "Last year" },
] as const satisfies readonly {
  window: PipelineRelativeWindow;
  label: string;
}[];

export const ENTITY_SCOPE_VALUES = ["c", "cp", "p"] as const;

export const MIN_DEGREE_VALUES = [
  "associates",
  "bachelors",
  "masters",
  "phd",
] as const;

/** Bounds on the graduation-year selects, matching the backend's range. */
export const MIN_GRAD_YEAR = 1900;
export const MAX_GRAD_YEAR = 2100;

export type PipelineEnrollmentStatus = (typeof ENROLLMENT_STATUS_VALUES)[number];
export type PipelineOrigin = (typeof ORIGIN_VALUES)[number];
export type PipelineAtsSyncState = (typeof ATS_SYNC_STATE_VALUES)[number];
export type PipelineSocialNetwork = (typeof SOCIAL_NETWORK_VALUES)[number];
export type PipelineRelativeWindow = (typeof RELATIVE_WINDOW_VALUES)[number];
export type PipelineEntityScope = (typeof ENTITY_SCOPE_VALUES)[number];
export type PipelineMinDegree = (typeof MIN_DEGREE_VALUES)[number];

/** Tri-state control: `null` (absent from the URL) means "no constraint". */
export const TRISTATE_VALUES = ["yes", "no"] as const;
export type PipelineTristate = (typeof TRISTATE_VALUES)[number];

const QUERY_OPTIONS = {
  history: "replace" as const,
  scroll: false,
  shallow: true,
  clearOnDefault: true,
};

/**
 * Free-text terms are joined with `|`, not the `,` nuqs defaults to: a comma is
 * legitimate inside the values people filter on ("San Francisco, CA",
 * "University of California, Berkeley") and would split one term into two.
 * `|` never appears in a name, company, location, email, or profile URL, and
 * `normalizeTerm` strips it so a pasted value cannot smuggle a separator into
 * the URL either.
 */
const TERM_SEPARATOR = "|";

const termsParser = parseAsArrayOf(parseAsString, TERM_SEPARATOR).withDefault(
  [],
);

/** Ids and enum slugs never contain a comma, so the default separator is safe. */
const idsParser = parseAsArrayOf(parseAsString).withDefault([]);

const enrollmentStatusesParser = parseAsArrayOf(
  parseAsStringLiteral(ENROLLMENT_STATUS_VALUES),
).withDefault([]);
/**
 * Origins, with a file type from a link saved before the grouping read as the
 * `profile_import` it is now counted under. Read here rather than at the section
 * so the panel can show it checked — and so unchecking it clears the URL, which
 * a value the checklist doesn't offer could not.
 *
 * De-duped, because two file types fold into one option: the panel offers one
 * checkbox for them and counts one filter, so the state behind it is one value.
 */
const originsList = parseAsArrayOf(
  createParser({
    parse: (value: string) => {
      if (!(ORIGIN_VALUES as readonly string[]).includes(value)) return null;
      return PROFILE_IMPORT_ORIGINS.includes(value)
        ? "profile_import"
        : (value as PipelineOrigin);
    },
    serialize: (value: PipelineOrigin) => value,
  }),
);
const originsParser = createParser({
  parse: (value: string) => {
    const origins = originsList.parse(value);
    return origins && [...new Set(origins)];
  },
  serialize: originsList.serialize,
  eq: originsList.eq,
}).withDefault([]);
const atsSyncStatesParser = parseAsArrayOf(
  parseAsStringLiteral(ATS_SYNC_STATE_VALUES),
).withDefault([]);
const socialNetworksParser = parseAsArrayOf(
  parseAsStringLiteral(SOCIAL_NETWORK_VALUES),
).withDefault([]);

const tristateParser = parseAsStringLiteral(TRISTATE_VALUES);
const windowParser = parseAsStringLiteral(RELATIVE_WINDOW_VALUES);

/**
 * One end of a time range, as one of three things:
 *
 * - `now-7d` — relative, and the reason a shared link keeps meaning "the last
 *   week". Only the windows the backend can resolve are accepted.
 * - `now` — the moment of the query.
 * - `2026-08-16` or `2026-08-16T14:30` — a fixed day, optionally to the minute.
 *   A bare day is read as the edge of that day, so the older day-only links
 *   still resolve to the range they always did.
 *
 * Anything else parses to `null` rather than being forwarded, so a hand-edited
 * URL cannot push an unparseable bound into the request body.
 */
const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}(T([01]\d|2[0-3]):[0-5]\d)?$/;

export function parseInstantToken(value: string): string | null {
  if (value === "now") return value;
  const relative = /^now-(.+)$/.exec(value);
  if (relative) {
    return (RELATIVE_WINDOW_VALUES as readonly string[]).includes(relative[1])
      ? value
      : null;
  }
  if (!INSTANT_PATTERN.test(value)) return null;
  // Rejects the days a pattern can't ("2026-02-31"), which would otherwise roll
  // over into March. Compared component-wise rather than through an ISO string,
  // which would shift the day in any zone west or east of UTC.
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? value
    : null;
}

const instantParser = createParser({
  parse: parseInstantToken,
  serialize: (value: string) => value,
});

/** `YYYY` or `YYYY-MM` — what the month/year picker and the API both speak. */
const MONTH_PATTERN = /^\d{4}(-(0[1-9]|1[0-2]))?$/;

/**
 * A company or university chip: a name plus the knobs its card exposes.
 *
 * `domain` is carried for the logo only and is stripped before the request —
 * the API forbids unknown fields.
 */
const entitySchema = z.object({
  text: z.string().trim().min(1).max(MAX_FILTER_TERM_LENGTH),
  mode: z.enum(["include", "exclude"]).default("include"),
  required: z.boolean().default(false),
  scope: z.enum(ENTITY_SCOPE_VALUES).default("cp"),
  from_date: z.string().regex(MONTH_PATTERN).nullish(),
  to_date: z.string().regex(MONTH_PATTERN).nullish(),
  domain: z.string().max(253).nullish(),
});

export type PipelineEntity = z.output<typeof entitySchema>;

/**
 * Entity chips ride the URL as JSON rather than a delimited string: each one
 * carries a mode, a scope, and two optional dates, and inventing a separator
 * scheme for that would collide with the punctuation in real company names. A
 * malformed value fails validation and falls back to "no chips", so a
 * hand-edited URL can't push an unparseable entity into the request body.
 */
const entitiesParser = parseAsJson(
  z.array(entitySchema).max(MAX_FILTER_TERMS).parse,
).withDefault([] as PipelineEntity[]);

const minDegreeParser = parseAsStringLiteral(MIN_DEGREE_VALUES);

export const pipelineFilterParsers = {
  names: termsParser,
  emails: termsParser,
  jobTitles: termsParser,
  locations: termsParser,
  skills: termsParser,
  majors: termsParser,
  universityLocations: termsParser,
  socialUrls: termsParser,

  excludeNames: termsParser,
  excludeEmails: termsParser,
  excludeJobTitles: termsParser,
  excludeLocations: termsParser,
  excludeSkills: termsParser,
  excludeMajors: termsParser,
  excludeUniversityLocations: termsParser,
  excludeSocialUrls: termsParser,

  companies: entitiesParser,
  universities: entitiesParser,

  minDegree: minDegreeParser,
  gradYearStart: parseAsInteger,
  gradYearEnd: parseAsInteger,

  stages: idsParser,
  excludeStages: idsParser,
  sequences: idsParser,
  excludeSequences: idsParser,
  enrollmentStatuses: enrollmentStatusesParser,
  origins: originsParser,
  atsSyncStates: atsSyncStatesParser,
  socialNetworks: socialNetworksParser,
  excludeSocialNetworks: socialNetworksParser,

  enrolled: tristateParser,
  revealed: tristateParser,
  hasEmail: tristateParser,
  hasPhone: tristateParser,

  addedFrom: instantParser,
  addedTo: instantParser,
  addedWithin: windowParser,
  inStageFrom: instantParser,
  inStageTo: instantParser,
  inStageWithin: windowParser,
  minExperienceYears: parseAsInteger,
  maxExperienceYears: parseAsInteger,
} as const;

export const pipelineFilterUrlKeys = {
  jobTitles: "titles",
  socialUrls: "socials",
  universityLocations: "uni_locations",
  excludeNames: "names_not",
  excludeEmails: "emails_not",
  excludeJobTitles: "titles_not",
  excludeLocations: "locations_not",
  excludeSkills: "skills_not",
  excludeMajors: "majors_not",
  excludeUniversityLocations: "uni_locations_not",
  excludeSocialUrls: "socials_not",
  minDegree: "min_degree",
  gradYearStart: "grad_from",
  gradYearEnd: "grad_to",
  excludeStages: "stages_not",
  excludeSequences: "sequences_not",
  enrollmentStatuses: "enrollment_status",
  atsSyncStates: "sync_state",
  socialNetworks: "networks",
  excludeSocialNetworks: "networks_not",
  hasEmail: "has_email",
  hasPhone: "has_phone",
  addedFrom: "added_from",
  addedTo: "added_to",
  addedWithin: "added_within",
  inStageFrom: "in_stage_from",
  inStageTo: "in_stage_to",
  inStageWithin: "in_stage_within",
  minExperienceYears: "exp_min",
  maxExperienceYears: "exp_max",
} as const;

export type PipelineFilterState = {
  names: string[];
  emails: string[];
  jobTitles: string[];
  locations: string[];
  skills: string[];
  majors: string[];
  universityLocations: string[];
  socialUrls: string[];
  excludeNames: string[];
  excludeEmails: string[];
  excludeJobTitles: string[];
  excludeLocations: string[];
  excludeSkills: string[];
  excludeMajors: string[];
  excludeUniversityLocations: string[];
  excludeSocialUrls: string[];
  companies: PipelineEntity[];
  universities: PipelineEntity[];
  minDegree: PipelineMinDegree | null;
  gradYearStart: number | null;
  gradYearEnd: number | null;
  stages: string[];
  excludeStages: string[];
  sequences: string[];
  excludeSequences: string[];
  enrollmentStatuses: PipelineEnrollmentStatus[];
  origins: PipelineOrigin[];
  atsSyncStates: PipelineAtsSyncState[];
  socialNetworks: PipelineSocialNetwork[];
  excludeSocialNetworks: PipelineSocialNetwork[];
  enrolled: PipelineTristate | null;
  revealed: PipelineTristate | null;
  hasEmail: PipelineTristate | null;
  hasPhone: PipelineTristate | null;
  addedFrom: string | null;
  addedTo: string | null;
  addedWithin: PipelineRelativeWindow | null;
  inStageFrom: string | null;
  inStageTo: string | null;
  inStageWithin: PipelineRelativeWindow | null;
  minExperienceYears: number | null;
  maxExperienceYears: number | null;
};

export type PipelineFilterPatch = Partial<PipelineFilterState>;

// --------------------------------------------------------------------------- //
// Term fields
// --------------------------------------------------------------------------- //

/**
 * The two sides of one free-text field. A chip toggled to "exclude" moves from
 * `include` to `exclude`; the backend ORs the included terms and negates the
 * excluded ones, so a term can only ever be on one side.
 */
export type PipelineTermSet = {
  include: string[];
  exclude: string[];
};

/** Free-text fields, each backed by an `include` and an `exclude` URL param. */
export const PIPELINE_TERM_FIELDS = [
  "names",
  "emails",
  "jobTitles",
  "locations",
  "skills",
  "majors",
  "universityLocations",
  "socialUrls",
] as const;

export type PipelineTermField = (typeof PIPELINE_TERM_FIELDS)[number];

const EXCLUDE_KEYS: Record<PipelineTermField, keyof PipelineFilterState> = {
  names: "excludeNames",
  emails: "excludeEmails",
  jobTitles: "excludeJobTitles",
  locations: "excludeLocations",
  skills: "excludeSkills",
  majors: "excludeMajors",
  universityLocations: "excludeUniversityLocations",
  socialUrls: "excludeSocialUrls",
};

/** Read one field's chips out of the URL state. */
export function termSet(
  state: PipelineFilterState,
  field: PipelineTermField,
): PipelineTermSet {
  return {
    include: state[field],
    exclude: state[EXCLUDE_KEYS[field]] as string[],
  };
}

/** Patch for writing one field's chips back. */
export function termPatch(
  field: PipelineTermField,
  next: PipelineTermSet,
): PipelineFilterPatch {
  return {
    [field]: next.include,
    [EXCLUDE_KEYS[field]]: next.exclude,
  } as PipelineFilterPatch;
}

/** Chips on a field, both sides — what a section badge counts. */
export function termCount(
  state: PipelineFilterState,
  ...fields: PipelineTermField[]
): number {
  return fields.reduce((total, field) => {
    const set = termSet(state, field);
    return total + set.include.length + set.exclude.length;
  }, 0);
}

export const EMPTY_TERM_SET: PipelineTermSet = { include: [], exclude: [] };

// --------------------------------------------------------------------------- //
// Entity chips (companies, universities)
// --------------------------------------------------------------------------- //

/** Fields holding entity chips rather than plain terms. */
export type PipelineEntityField = "companies" | "universities";

/**
 * Add a chip. Names are compared case-insensitively so picking a suggestion the
 * user already typed by hand doesn't produce two identical cards.
 */
export function addEntity(
  entities: PipelineEntity[],
  input: { text: string; domain?: string | null },
): PipelineEntity[] {
  const text = normalizeTerm(input.text);
  if (!text) return entities;
  if (entities.some((entity) => sameTerm(entity.text, text))) return entities;
  if (entities.length >= MAX_FILTER_TERMS) return entities;
  return [
    ...entities,
    {
      text,
      mode: "include",
      required: false,
      scope: "cp",
      from_date: null,
      to_date: null,
      domain: input.domain ?? null,
    },
  ];
}

export function updateEntity(
  entities: PipelineEntity[],
  index: number,
  patch: Partial<PipelineEntity>,
): PipelineEntity[] {
  return entities.map((entity, i) => (i === index ? { ...entity, ...patch } : entity));
}

export function removeEntity(
  entities: PipelineEntity[],
  index: number,
): PipelineEntity[] {
  return entities.filter((_, i) => i !== index);
}

// --------------------------------------------------------------------------- //
// Term hygiene
// --------------------------------------------------------------------------- //

/**
 * Collapse whitespace, strip the URL separator, and cap length — the same
 * normalisation the backend applies, done here so a chip reads back exactly as
 * it will be matched. Returns `null` for anything that would be a no-op.
 */
export function normalizeTerm(raw: string): string | null {
  const term = raw.split(TERM_SEPARATOR).join(" ").split(/\s+/).join(" ").trim();
  if (!term) return null;
  return term.slice(0, MAX_FILTER_TERM_LENGTH);
}

function sameTerm(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Add a term as included. De-duped case-insensitively across *both* sides — a
 * term already excluded is not re-added as included, which would ask the backend
 * for rows that match it and don't.
 */
export function addTerm(set: PipelineTermSet, raw: string): PipelineTermSet {
  const term = normalizeTerm(raw);
  if (!term) return set;
  if ([...set.include, ...set.exclude].some((existing) => sameTerm(existing, term))) {
    return set;
  }
  if (set.include.length >= MAX_FILTER_TERMS) return set;
  return { ...set, include: [...set.include, term] };
}

export function removeTerm(set: PipelineTermSet, term: string): PipelineTermSet {
  return {
    include: set.include.filter((existing) => !sameTerm(existing, term)),
    exclude: set.exclude.filter((existing) => !sameTerm(existing, term)),
  };
}

/** Move a term between the included and excluded sides. */
export function toggleTermExcluded(
  set: PipelineTermSet,
  term: string,
): PipelineTermSet {
  const excluded = set.exclude.some((existing) => sameTerm(existing, term));
  const stripped = removeTerm(set, term);
  if (excluded) {
    if (stripped.include.length >= MAX_FILTER_TERMS) return set;
    return { ...stripped, include: [...stripped.include, term] };
  }
  if (stripped.exclude.length >= MAX_FILTER_TERMS) return set;
  return { ...stripped, exclude: [...stripped.exclude, term] };
}

/** Toggle membership in a fixed-vocabulary list, capped at the backend limit. */
export function toggleValue<T extends string>(current: T[], value: T): T[] {
  if (current.includes(value)) return current.filter((v) => v !== value);
  if (current.length >= MAX_FILTER_IDS) return current;
  return [...current, value];
}

// --------------------------------------------------------------------------- //
// URL state -> request body
// --------------------------------------------------------------------------- //

function terms(value: string[]): string[] | undefined {
  const cleaned = value
    .map(normalizeTerm)
    .filter((t): t is string => t !== null)
    .slice(0, MAX_FILTER_TERMS);
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Ids and enum slugs, capped and de-duped — two of the same value in the URL is
 * one value to the query, and grouping (a file type read as `profile_import`)
 * can produce a pair from a legacy link.
 */
function list<T>(value: T[]): T[] | undefined {
  const unique = [...new Set(value)].slice(0, MAX_FILTER_IDS);
  return unique.length > 0 ? unique : undefined;
}

/** Drop the logo-only `domain` and any blank date the card left behind. */
function entities(value: PipelineEntity[]): PipelineEntityFilter[] | undefined {
  const cleaned = value
    .map((entity) => {
      const text = normalizeTerm(entity.text);
      if (!text) return null;
      const from = entity.from_date || undefined;
      const to = entity.to_date || undefined;
      // An inverted window is a normal intermediate state in a month picker,
      // so widen to "any time" rather than sending a body the API rejects.
      const ordered = !from || !to || from <= to;
      const cleaned: PipelineEntityFilter = {
        text,
        mode: entity.mode,
        required: entity.required,
        scope: entity.scope,
        from_date: ordered ? from : undefined,
        to_date: ordered ? to : undefined,
      };
      return cleaned;
    })
    .filter((entity): entity is PipelineEntityFilter => entity !== null)
    .slice(0, MAX_FILTER_TERMS);
  return cleaned.length > 0 ? cleaned : undefined;
}

function gradYear(value: number | null): number | undefined {
  if (value === null) return undefined;
  const year = Math.trunc(value);
  if (year < MIN_GRAD_YEAR || year > MAX_GRAD_YEAR) return undefined;
  return year;
}

function tristate(value: PipelineTristate | null): boolean | undefined {
  if (value === null) return undefined;
  return value === "yes";
}

const RELATIVE_MS: Record<PipelineRelativeWindow, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "14d": 14 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "60d": 60 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
  "180d": 180 * 24 * 60 * 60 * 1000,
  "365d": 365 * 24 * 60 * 60 * 1000,
};

/** The window in `now-7d`, or `null` for a fixed or absent bound. */
export function relativeWindowOf(
  token: string | null,
): PipelineRelativeWindow | null {
  if (!token?.startsWith("now-")) return null;
  const window = token.slice(4);
  return (RELATIVE_WINDOW_VALUES as readonly string[]).includes(window)
    ? (window as PipelineRelativeWindow)
    : null;
}

/**
 * A relative bound as the fixed local instant it currently points at, for when a
 * range stops being open-ended and the sliding lower bound has to be pinned.
 * Absent and already-fixed bounds are returned untouched.
 */
export function toAbsoluteToken(token: string | null): string | null {
  const window = relativeWindowOf(token);
  if (!window && token !== "now") return token;
  const date = new Date();
  date.setSeconds(0, 0);
  if (window) date.setTime(date.getTime() - RELATIVE_MS[window]);
  return toLocalDateTimeToken(date);
}

/**
 * Resolve one end of a range to an instant.
 *
 * A bare day covers the whole day — `start` at midnight, `end` at its last
 * millisecond — and a time to the minute is likewise inclusive of that minute at
 * the upper end, so "to 14:30" doesn't drop the row stamped 14:30:45. Resolved
 * in the viewer's timezone, so "added today" means their today.
 *
 * `now` is minute-quantised: an unquantised value would be a new number on every
 * render, and the compiled filter is a React Query key.
 */
function instant(token: string | null, edge: "start" | "end"): string | undefined {
  if (!token) return undefined;
  if (token === "now" || relativeWindowOf(token)) {
    const now = new Date();
    now.setSeconds(0, 0);
    const window = relativeWindowOf(token);
    return new Date(
      now.getTime() - (window ? RELATIVE_MS[window] : 0),
    ).toISOString();
  }
  const [day, time] = token.split("T");
  const [year, month, date] = day.split("-").map(Number);
  const [hours, minutes] = time ? time.split(":").map(Number) : [];
  const resolved =
    time === undefined
      ? edge === "start"
        ? new Date(year, month - 1, date, 0, 0, 0, 0)
        : new Date(year, month - 1, date, 23, 59, 59, 999)
      : edge === "start"
        ? new Date(year, month - 1, date, hours, minutes, 0, 0)
        : new Date(year, month - 1, date, hours, minutes, 59, 999);
  return Number.isNaN(resolved.getTime()) ? undefined : resolved.toISOString();
}

/**
 * One time range, compiled the way the backend can hold it steady.
 *
 * A relative lower bound goes over as the backend's own `*_within` window rather
 * than as an instant this client resolved: the server re-resolves it per query,
 * so the window keeps sliding and the request body — which is also a React Query
 * key — stops changing as the clock moves. It rides *alongside* a fixed upper
 * bound, which the backend applies independently.
 */
function timeRange(
  from: string | null,
  to: string | null,
): {
  from?: string;
  to?: string;
  within?: PipelineRelativeWindow;
} {
  const relative = relativeWindowOf(from);
  const lower = relative ? undefined : instant(from, "start");
  const upper = instant(upperBoundOf(to), "end");
  // Dropped rather than sent when inverted: the backend rejects `from > to` with
  // a 422, and a half-typed range is a normal state in a picker, not an error.
  if (lower !== undefined && upper !== undefined && lower > upper) return {};
  return { from: lower, to: upper, within: relative ?? undefined };
}

/**
 * The lower bound of a range, reading a legacy `*_within` param as the relative
 * bound it always meant.
 *
 * One rule, used by the panel and by the compiler, so the range that is applied
 * is the range the panel shows. A link carrying both a window and a fixed lower
 * bound resolves to the fixed one: the panel has a single lower bound to show,
 * and a second one applying invisibly is a filter nobody can see or clear.
 */
export function lowerBoundOf(
  from: string | null,
  window: PipelineRelativeWindow | null,
): string | null {
  return from ?? (window ? `now-${window}` : null);
}

/**
 * The upper bound of a range, with "now" read as the open end it is.
 *
 * Nothing joins a pipeline or enters a stage in the future, so `<= now` selects
 * the same rows as no upper bound at all — and sending it would pin the query key
 * to the current minute. The panel reads the bound through here too, so a link
 * carrying `now` doesn't count towards a section's badge while filtering nothing.
 */
export function upperBoundOf(to: string | null): string | null {
  return to === "now" ? null : to;
}

function years(value: number | null): number | undefined {
  if (value === null) return undefined;
  return Math.min(Math.max(Math.trunc(value), 0), MAX_EXPERIENCE_YEARS);
}

function ordered<T extends string | number>(
  from: T | undefined,
  to: T | undefined,
): boolean {
  return from === undefined || to === undefined || from <= to;
}

export type PipelineFilterCompileOptions = {
  /**
   * Whether the stage chips apply. The stage bar already scopes the request to
   * one stage, and the panel only offers stage chips on "All", so a stage
   * selection there must not narrow a single-stage view further.
   */
  stageFiltersApply?: boolean;
};

/**
 * Compile the URL state into a request body, or `null` when nothing is
 * constrained — an unfiltered pipeline posts no filter object at all.
 *
 * An inverted range is dropped rather than sent: the backend rejects
 * `from > to` with a 422, and a half-typed range is a normal intermediate state
 * in a date picker, not an error worth surfacing as a failed request.
 */
export function toPipelineFilterSchema(
  state: PipelineFilterState,
  options: PipelineFilterCompileOptions = {},
): PipelineFilterSchema | null {
  const { stageFiltersApply = true } = options;
  const added = timeRange(
    lowerBoundOf(state.addedFrom, state.addedWithin),
    state.addedTo,
  );
  const inStage = timeRange(
    lowerBoundOf(state.inStageFrom, state.inStageWithin),
    state.inStageTo,
  );
  const minYears = years(state.minExperienceYears);
  const maxYears = years(state.maxExperienceYears);

  const yearsValid = ordered(minYears, maxYears);

  const gradFrom = gradYear(state.gradYearStart);
  const gradTo = gradYear(state.gradYearEnd);
  const gradValid = ordered(gradFrom, gradTo);

  const schema: PipelineFilterSchema = {
    names: terms(state.names),
    emails: terms(state.emails),
    job_titles: terms(state.jobTitles),
    locations: terms(state.locations),
    skills: terms(state.skills),
    majors: terms(state.majors),
    university_locations: terms(state.universityLocations),
    social_urls: terms(state.socialUrls),

    exclude_names: terms(state.excludeNames),
    exclude_emails: terms(state.excludeEmails),
    exclude_job_titles: terms(state.excludeJobTitles),
    exclude_locations: terms(state.excludeLocations),
    exclude_skills: terms(state.excludeSkills),
    exclude_majors: terms(state.excludeMajors),
    exclude_university_locations: terms(state.excludeUniversityLocations),
    exclude_social_urls: terms(state.excludeSocialUrls),

    companies: entities(state.companies),
    universities: entities(state.universities),

    min_degree: state.minDegree ?? undefined,
    grad_year_start: gradValid ? gradFrom : undefined,
    grad_year_end: gradValid ? gradTo : undefined,

    stages: stageFiltersApply ? list(state.stages) : undefined,
    exclude_stages: stageFiltersApply ? list(state.excludeStages) : undefined,
    sequences: list(state.sequences),
    exclude_sequences: list(state.excludeSequences),
    enrollment_statuses: list(state.enrollmentStatuses),
    origins: list(state.origins),
    ats_sync_states: list(state.atsSyncStates),
    social_networks: list(state.socialNetworks),
    exclude_social_networks: list(state.excludeSocialNetworks),

    enrolled: tristate(state.enrolled),
    revealed: tristate(state.revealed),
    has_email: tristate(state.hasEmail),
    has_phone: tristate(state.hasPhone),

    added_from: added.from,
    added_to: added.to,
    added_within: added.within,
    in_stage_from: inStage.from,
    in_stage_to: inStage.to,
    in_stage_within: inStage.within,
    min_experience_years: yearsValid ? minYears : undefined,
    max_experience_years: yearsValid ? maxYears : undefined,
  };

  for (const key of Object.keys(schema) as (keyof PipelineFilterSchema)[]) {
    if (schema[key] === undefined) delete schema[key];
  }

  return Object.keys(schema).length > 0 ? schema : null;
}

/**
 * Keys that read as one applied filter together, as they're presented: the ends
 * of a range (with the relative window that can stand in for its lower end), and
 * the two sides of a free-text field.
 */
const GROUPS: (keyof PipelineFilterSchema)[][] = [
  ["added_from", "added_to", "added_within"],
  ["in_stage_from", "in_stage_to", "in_stage_within"],
  ["min_experience_years", "max_experience_years"],
  ["grad_year_start", "grad_year_end"],
  ["names", "exclude_names"],
  ["emails", "exclude_emails"],
  ["job_titles", "exclude_job_titles"],
  ["locations", "exclude_locations"],
  ["skills", "exclude_skills"],
  ["majors", "exclude_majors"],
  ["university_locations", "exclude_university_locations"],
  ["social_urls", "exclude_social_urls"],
  ["stages", "exclude_stages"],
  ["sequences", "exclude_sequences"],
  ["social_networks", "exclude_social_networks"],
];

/** Number of constrained *dimensions* — the badge count, not a term count. */
export function countAppliedPipelineFilters(
  state: PipelineFilterState,
  options: PipelineFilterCompileOptions = {},
): number {
  const schema = toPipelineFilterSchema(state, options);
  if (!schema) return 0;

  let count = 0;
  const grouped = new Set<string>();
  for (const keys of GROUPS) {
    if (keys.some((key) => schema[key] !== undefined)) count += 1;
    for (const key of keys) grouped.add(key);
  }
  for (const key of Object.keys(schema)) {
    if (!grouped.has(key)) count += 1;
  }
  return count;
}

export function hasAnyPipelineFilterApplied(
  state: PipelineFilterState,
  options: PipelineFilterCompileOptions = {},
): boolean {
  return toPipelineFilterSchema(state, options) !== null;
}

// --------------------------------------------------------------------------- //
// Hooks
// --------------------------------------------------------------------------- //

/** Raw URL state. Prefer `usePipelineFilterPanel` for anything that writes. */
export function usePipelineFilters() {
  return useQueryStates(pipelineFilterParsers, {
    urlKeys: pipelineFilterUrlKeys,
    ...QUERY_OPTIONS,
  });
}

export type PipelineFilterPanelApi = {
  state: PipelineFilterState;
  /** Patch one or more fields; resets the table to page 1. */
  set: (patch: PipelineFilterPatch) => void;
  clearAll: () => void;
  appliedCount: number;
  hasAnyApplied: boolean;
  /** False while the stage bar has a single stage selected. */
  stageFiltersApply: boolean;
};

/**
 * Read/write access for the filter sections.
 *
 * Every write resets pagination: the current page may not exist in the narrowed
 * result set, and a filtered view that lands on an empty page 4 looks like the
 * filter matched nothing.
 */
export function usePipelineFilterPanel(): PipelineFilterPanelApi {
  const [state, setState] = usePipelineFilters();
  const setPage = usePipelineTableStore((s) => s.setPage);
  const stageFiltersApply =
    usePipelineViewStore((s) => s.selectedStageId) === null;

  const set = useCallback(
    (patch: PipelineFilterPatch) => {
      setPage(1);
      void setState(patch);
    },
    [setPage, setState],
  );

  const clearAll = useCallback(() => {
    setPage(1);
    void setState(null);
  }, [setPage, setState]);

  // Leaving "All" drops the stage chips outright rather than parking them: the
  // panel no longer shows a stage field there, and a hidden selection that
  // silently re-narrows the view on the way back is worse than losing it.
  const hasStageChips = state.stages.length > 0 || state.excludeStages.length > 0;
  useEffect(() => {
    if (stageFiltersApply || !hasStageChips) return;
    void setState({ stages: [], excludeStages: [] });
  }, [stageFiltersApply, hasStageChips, setState]);

  const options = useMemo(() => ({ stageFiltersApply }), [stageFiltersApply]);

  return {
    state,
    set,
    clearAll,
    appliedCount: countAppliedPipelineFilters(state, options),
    hasAnyApplied: hasAnyPipelineFilterApplied(state, options),
    stageFiltersApply,
  };
}

/**
 * Compile the URL filters and mirror them into the pipeline view store.
 *
 * Returns the same object identity while the compiled filters are unchanged, so
 * it is safe to use directly in a React Query key. Mounted once by the pipeline
 * view; the sections write to the URL and this is what turns those writes into
 * a request body.
 */
export function usePipelineFilterSync(): PipelineFilterSchema | null {
  const [state] = usePipelineFilters();
  const setFilters = usePipelineViewStore((s) => s.setFilters);
  const storedSignature = usePipelineViewStore((s) => s.filterSignature);
  const stageFiltersApply =
    usePipelineViewStore((s) => s.selectedStageId) === null;

  const signature = JSON.stringify(
    toPipelineFilterSchema(state, { stageFiltersApply }),
  );

  // Keyed on the signature, not on `state`: nuqs hands back a fresh object on
  // every render, so re-deriving on identity would give the query key a new
  // value each render and refetch forever.
  const filters = useMemo(
    () =>
      signature === "null"
        ? null
        : (JSON.parse(signature) as PipelineFilterSchema),
    [signature],
  );

  // The store is written from an effect (not during render) because its readers
  // are non-React — `buildPipelineActionBody()` and the export builder.
  //
  // The guard compares against the store rather than a local ref so the write
  // re-fires whenever something else resets the store: `ensureJobScope` clears
  // the filters on a job switch, and if the URL happened to carry the same
  // filters on the new job a ref-based guard would skip the rewrite, leaving a
  // filtered table whose select-all resolved the *unfiltered* pipeline.
  const target = filters ? signature : "";
  useEffect(() => {
    if (storedSignature === target) return;
    setFilters(filters, target);
  }, [filters, target, storedSignature, setFilters]);

  return filters;
}
